import Phaser from 'phaser';
import { FRUIT_TIERS } from '../config';

const SFX_KEYS = [
  'sfx-throw',
  'sfx-bounce',
  'sfx-merge-small',
  'sfx-merge-medium',
  'sfx-merge-large',
  'sfx-game-over',
  'sfx-new-best',
  'sfx-button-tap',
] as const;

const FRUIT_FRAME_COUNT = 4;

/**
 * Loads all sprites and tries to load every audio file (gracefully tolerating
 * missing files — the audio system falls back to placeholder SFX).
 */
export class PreloadScene extends Phaser.Scene {
  /** Audio keys that actually loaded — populated as `filecomplete-audio-*` fires. */
  readonly loadedAudio: Set<string> = new Set();

  constructor() {
    super('preload');
  }

  preload(): void {
    this.load.setBaseURL(import.meta.env.BASE_URL);
    for (const tier of FRUIT_TIERS) {
      this.load.image(`fruit-image-${tier.name}`, `assets/sprites/fruit-${tier.name}.png`);
    }
    this.load.image('hand-image', 'assets/sprites/hand.png');
    this.load.image('vfx-merge-poof', 'assets/sprites/vfx-merge-poof.png');

    // Audio loading is deferred until the user supplies real SFX files. Until
    // then `loadedAudio` stays empty and audioSystem falls back to procedural
    // placeholder SFX. Re-enable the loop below once files are dropped into
    // public/assets/audio/.
    void SFX_KEYS;

    this.load.on('progress', (frac: number) => {
      this.game.events.emit('preload-progress', frac);
    });
  }

  create(): void {
    for (const tier of FRUIT_TIERS) {
      const tex = this.textures.get(`fruit-image-${tier.name}`);
      const src = tex.getSourceImage(0) as HTMLImageElement;
      const frameW = Math.floor(src.width / FRUIT_FRAME_COUNT);
      const frameH = src.height;
      this.textures.remove(`fruit-image-${tier.name}`);
      this.textures.addSpriteSheet(`fruit-${tier.name}`, src, {
        frameWidth: frameW,
        frameHeight: frameH,
      });
    }
    const handTex = this.textures.get('hand-image').getSourceImage(0) as HTMLImageElement;
    const handFrameW = Math.floor(handTex.width / 4);
    this.textures.remove('hand-image');
    this.textures.addSpriteSheet('hand', handTex, {
      frameWidth: handFrameW,
      frameHeight: handTex.height,
    });

    this.registry.set('loaded-audio', this.loadedAudio);
    this.game.events.emit('preload-complete');

    this.scene.start('title');
  }
}
